import { useQuery } from "@tanstack/react-query";
import { Pie, PieChart, ResponsiveContainer } from "recharts";
import { handleFetchRequestsByRole } from "../../../api/autorequests";
import { useState } from "react";
import AllAutoRequestsDrawer from "./AllRequestsDrawer";
import { Empty } from "antd";

export default function RoleRequests({
  isAnimationActive = true,
  dateRange,
}: {
  isAnimationActive?: boolean;
  dateRange?: any;
}) {
  const [selected, setSelected] = useState<any>(null);
  const [detailsModal, setDetailsModal] = useState<boolean>(false);
  const { data: roleRequests } = useQuery({
    queryKey: ["roleRequests", dateRange?.startDate, dateRange?.endDate],
    queryFn: () =>
      handleFetchRequestsByRole({
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
      }),
  });

  function processTopRequests(requestData: any[]): any[] {
    const finalResults: any[] = [];

    if (!requestData || requestData.length === 0) {
      return finalResults;
    }

    const totalValue = requestData.reduce(
      (sum, request) => sum + request.value,
      0
    );

    if (totalValue === 0) {
      return requestData.map((req) => ({
        name: req.name,
        value: req.value,
        percentage: 0,
      }));
    }

    const sortedRequests = [...requestData].sort((a, b) => b.value - a.value);

    const topFiveRequests = sortedRequests.slice(0, 5);
    const remainingRequests = sortedRequests.slice(5);

    for (const request of topFiveRequests) {
      const percentage = (request.value / totalValue) * 100;
      finalResults.push({
        name: request.name,
        value: request.value,
        percentage: parseFloat(percentage.toFixed(2)),
      });
    }

    if (remainingRequests.length > 0) {
      const othersValue = remainingRequests.reduce(
        (sum, request) => sum + request.value,
        0
      );
      const othersPercentage = (othersValue / totalValue) * 100;

      finalResults.push({
        name: `Others (${remainingRequests.length} items)`,
        value: othersValue,
        percentage: parseFloat(othersPercentage.toFixed(2)),
      });
    }

    return finalResults;
  }

  const rankedRequests = processTopRequests(roleRequests?.data);

  return (
    <div className="">
      {roleRequests?.data?.length > 0 ? (
        <div className="w-full" style={{ height: "260px" }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart
              style={{
                width: "100%",
                height: "100%",
                maxWidth: "500px",
                maxHeight: "80vh",
                aspectRatio: 1,
              }}
            >
              <Pie
                data={roleRequests?.data || []}
                dataKey="value"
                cx="50%"
                cy="50%"
                outerRadius="50%"
                fill="#8884d8"
                isAnimationActive={isAnimationActive}
              />
              <Pie
                data={roleRequests?.data || []}
                dataKey="value"
                cx="50%"
                cy="50%"
                innerRadius="60%"
                outerRadius="80%"
                fill="#82ca9d"
                label
                isAnimationActive={isAnimationActive}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div
          className="text-gray-500 text-center py-4 p-2 m-1"
          style={{ height: "260px" }}
        >
          <Empty
            description="No Chart Data Found"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      )}
      <div className="">
        {rankedRequests?.map((item: any) => (
          <button
            key={item?._id}
            onClick={() => {
              setDetailsModal(true);
              setSelected(item);
            }}
            className="flex items-center justify-between border-t rounded-xl p-2 m-1 cursor-pointer hover:bg-blue-100 hover:border-blue-200 transition-all duration-300 w-full"
          >
            <div className="flex items-center">
              <div className="w-2 h-2 rounded-full bg-blue-500" />
              <span className="ml-2">{item?.name}</span>
            </div>
            <div className="">
              {item?.value} ({item?.percentage}%)
            </div>
          </button>
        ))}
        {roleRequests?.data?.length === 0 && (
          <div className="text-gray-500 text-center py-4 border-t rounded-xl p-2 m-1">
            <Empty description="No Requests for this period" />
          </div>
        )}
      </div>

      {detailsModal && (
        <AllAutoRequestsDrawer
          title={`Requests by Role: ${selected?.name}`}
          isVisible={detailsModal}
          setVisible={setDetailsModal}
          selected={selected}
          designation={selected?.name.includes("Others") ? "" : selected?.name}
        />
      )}
    </div>
  );
}
